import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'

import AuthGuard from './components/auth/AuthGuard'
import { VanityOrgGate } from './components/auth/VanityOrgGate'
import { EMPLOYEES_ROOT } from './components/internal/team-config/paths'
import { TeamConfigLayout } from './components/internal/team-config/TeamConfigLayout'
import { teamConfigSectionRoutes } from './components/internal/team-config/teamConfigRoutes'
import { AppLayout } from './components/layout/AppLayout'
import { SETTINGS_ROOT, settingsPath } from './components/settings/sections'
import { SettingsLayout } from './components/settings/SettingsLayout'
import { settingsSectionRoutes } from './components/settings/settingsRoutes'
import { TeamGuard } from './components/shared/TeamGuard'
import { Toaster } from './components/ui/sonner'
import { useAuth } from './contexts/AuthContext'
import { TeamProvider, useTeam } from './contexts/TeamContext'
import { isNavRouteVisible, resolveHomeRedirect } from './lib/nav'
import AlphaGatePage from './pages/AlphaGatePage'
import BatchPage from './pages/BatchPage'
import BenchmarkPage from './pages/BenchmarkPage'
import BenchmarkTaskPage from './pages/BenchmarkTaskPage'
import ConversationPage from './pages/ConversationPage'
import DatasetDetailPage from './pages/DatasetDetailPage'
import DatasetEditPage from './pages/DatasetEditPage'
import DashboardPage from './pages/DashboardPage'
import DataRefineryPage from './pages/DataRefineryPage'
import FailureRecordPage from './pages/FailureRecordPage'
import DatasetsListPage from './pages/DatasetsListPage'
import EmployeesPage from './pages/EmployeesPage'
import EvalAnalysisPage from './pages/EvalAnalysisPage'
import EvalGroupPage from './pages/EvalGroupPage'
import EvalRunPage from './pages/EvalRunPage'
import EvalsListPage from './pages/EvalsListPage'
import EvalTaskPage from './pages/EvalTaskPage'
import ModelDetailPage from './pages/ModelDetailPage'
import ModelMetricsPage from './pages/ModelMetricsPage'
import ModelsListPage from './pages/ModelsListPage'
import NotFoundPage from './pages/NotFoundPage'
import PipelinePage from './pages/PipelinePage'
import ReviewPage from './pages/ReviewPage'
import TrajectoriesPage from './pages/TrajectoriesPage'
import TrainingPage from './pages/TrainingPage'

/**
 * EmployeeRoute — renders children only when the authenticated user is a
 * TrainLoop employee. Redirects to / otherwise (no flash: useTeam() is
 * always mounted inside TeamProvider at this point).
 */
function EmployeeRoute({ children }: { children: React.ReactNode }): React.JSX.Element | null {
  const { isEmployee, meLoaded } = useTeam()
  // isEmployee is false while /me is still in flight, so deciding before it
  // loads would bounce employees off deep links and refreshes. Hold blank
  // until the flag is real — same pattern as HomeRoute waiting on
  // landingPage.
  if (!meLoaded) {
    return null
  }
  if (!isEmployee) {
    return <Navigate to="/" replace />
  }
  return <>{children}</>
}

/**
 * RestrictedRoute — blocks a route on restricted demo teams (TRA-832).
 * Redirects to / so settings and create/edit flows are unreachable even by
 * URL. The API independently 403s non-employee mutations; this guard exists
 * so a live demo (driven by an exempt employee) can't wander into them.
 */
function RestrictedRoute({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { isRestricted } = useTeam()
  if (isRestricted) {
    return <Navigate to="/" replace />
  }
  return <>{children}</>
}

/**
 * NavRoute — renders children only when `navRoute` is visible in the
 * team's sidebar config (TRA-927). Hiding a nav entry makes its page
 * unreachable by URL too. /refinery (the tasks the gate proved for the
 * team, TRA-1477) and /training (its training runs) both read real data and
 * keep the gate so each page stays hidden until staff unhide it for a
 * team that has something to show. `navRoute` is the NAV_ENTRIES key.
 */
function NavRoute({
  navRoute,
  children,
}: {
  navRoute: string
  children: React.ReactNode
}): React.JSX.Element | null {
  const { hiddenNavItems, navConfigLoaded } = useTeam()
  if (!isNavRouteVisible({ to: navRoute, hiddenNavItems, navConfigLoaded })) {
    // Blank, not a redirect, while the nav config is still in flight: a
    // deep link landing here is legitimate for a team that CAN see the
    // page, and bouncing it home before the config arrives would strand
    // it. A failed load therefore blanks these pages permanently — the
    // fail-closed side, which is the right one for a page hidden on purpose.
    return navConfigLoaded ? <Navigate to="/" replace /> : null
  }
  return <>{children}</>
}

/**
 * HomeRoute — "/" honors the team's nav config (TRA-927); the redirect
 * decision lives in resolveHomeRedirect (lib/nav.ts). Entry = React
 * Router's initial location (key "default") — a Dashboard nav click is a
 * pushed navigation with a fresh key, so Dashboard stays reachable when it
 * isn't hidden. Team switches and impersonation reload the page, so the
 * new team's landing page re-applies.
 * Renders nothing until /me resolves the config — a Dashboard flash that
 * then redirects is worse than a beat of blank.
 */
function HomeRoute(): React.JSX.Element | null {
  const { hiddenNavItems, landingPage } = useTeam()
  const location = useLocation()
  if (landingPage === null) {
    return null
  }
  const target = resolveHomeRedirect({
    hiddenNavItems,
    landingPage,
    isEntry: location.key === 'default',
  })
  if (target !== null) {
    return <Navigate to={target} replace />
  }
  return <DashboardPage />
}

/**
 * MovedToSettings — the old top-level /billing and /prompts paths redirect
 * into Settings (TRA-1185) so bookmarks keep working, and the old
 * /settings/train-loop address lands on Settings > Pipeline (TRA-1557).
 * The query string travels along: /prompts?trace= from an old "Fix prompt"
 * link still lands on the right card.
 */
function MovedToSettings({ section }: { section: string }): React.JSX.Element {
  const { search } = useLocation()
  return <Navigate to={{ pathname: settingsPath(section), search }} replace />
}

/**
 * AppRoutes — rendered after auth + team context are ready.
 * Alpha-blocked users are intercepted before TeamProvider so this
 * component is only reached by fully valid users.
 */
function AppRoutes(): React.JSX.Element {
  return (
    <TeamProvider>
      <TeamGuard>
        <Routes>
          <Route element={<AppLayout><Outlet /></AppLayout>}>
            {/* Default landing → the team dashboard (TRA-868), unless the
                team's nav config says otherwise (TRA-927). */}
            <Route path="/" element={<HomeRoute />} />

            <Route path="/models" element={<ModelsListPage />} />
            {/* Pipelines are the other deployment type (TRA-863) and live
                under Models the way benchmarks live under Evals. */}
            <Route path="/models/pipelines/:name" element={<PipelinePage />} />
            <Route
              path="/models/pipelines/:name/batches/:batchId"
              element={<BatchPage />}
            />
            <Route path="/models/:modelId" element={<ModelDetailPage />} />
            <Route path="/models/:modelId/metrics" element={<ModelMetricsPage />} />

            <Route path="/evals" element={<EvalsListPage />} />
            <Route path="/evals/groups/:slug" element={<EvalGroupPage />} />
            <Route path="/evals/groups/:slug/analysis" element={<EvalAnalysisPage />} />
            <Route path="/evals/groups/:slug/tasks/:taskSlug" element={<EvalTaskPage />} />
            <Route
              path="/evals/groups/:slug/tasks/:taskSlug/runs/:runId"
              element={<EvalRunPage />}
            />

            {/* Benchmark evals (failure analysis) live under /evals too —
                one concept, richer detail pages for bucket-backed corpora. */}
            <Route path="/evals/benchmarks/:slug" element={<BenchmarkPage />} />
            <Route path="/evals/benchmarks/:slug/tasks/:taskSlug" element={<BenchmarkTaskPage />} />

            <Route path="/review" element={<ReviewPage />} />

            <Route path="/trajectories" element={<TrajectoriesPage />} />

            {/* Real data since TRA-1172 (the team's own training tasks),
                still hidden per team until staff unhide it for a team that
                has tasks (Employees page). */}
            <Route
              path="/refinery"
              element={
                <NavRoute navRoute="/refinery">
                  <DataRefineryPage />
                </NavRoute>
              }
            />
            {/* One failure record of the team (the failure analysis views),
                behind the refinery's own gate. */}
            <Route
              path="/refinery/records/:recordId"
              element={
                <NavRoute navRoute="/refinery">
                  <FailureRecordPage />
                </NavRoute>
              }
            />
            <Route
              path="/training"
              element={
                <NavRoute navRoute="/training">
                  <TrainingPage />
                </NavRoute>
              }
            />

            <Route path="/conversations" element={<ConversationPage />} />

            <Route path="/datasets" element={<DatasetsListPage />} />
            <Route
              path="/datasets/new"
              element={
                <RestrictedRoute>
                  <DatasetEditPage mode="create" />
                </RestrictedRoute>
              }
            />
            <Route path="/datasets/:datasetId" element={<DatasetDetailPage />} />
            <Route
              path="/datasets/:datasetId/edit"
              element={
                <RestrictedRoute>
                  <DatasetEditPage mode="edit" />
                </RestrictedRoute>
              }
            />

            {/* Settings is a layout route with one child per registry entry
                (components/settings/sections.ts), so adding a section never
                touches this table. Restricted demo teams are turned away from
                all of it, Prompts included (TRA-832). */}
            <Route
              path={SETTINGS_ROOT}
              element={
                <RestrictedRoute>
                  <SettingsLayout />
                </RestrictedRoute>
              }
            >
              {settingsSectionRoutes()}
            </Route>
            <Route path="/billing" element={<MovedToSettings section="billing" />} />
            <Route path="/prompts" element={<MovedToSettings section="prompts" />} />
            {/* Settings > Train loop is now Settings > Pipeline (TRA-1557). */}
            <Route path={settingsPath('train-loop')} element={<MovedToSettings section="pipeline" />} />

            {/* The staff area: the team list at /employees and, under
                teams/:teamId, one configuration page per registry entry
                (components/internal/team-config/sections.ts), so adding a
                page never touches this table. Restricted demo teams are
                turned away from all of it (TRA-832). */}
            <Route
              path={EMPLOYEES_ROOT}
              element={
                <RestrictedRoute>
                  <EmployeeRoute>
                    <Outlet />
                  </EmployeeRoute>
                </RestrictedRoute>
              }
            >
              <Route index element={<EmployeesPage />} />
              <Route path="teams/:teamId" element={<TeamConfigLayout />}>
                {teamConfigSectionRoutes()}
              </Route>
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </TeamGuard>
    </TeamProvider>
  )
}

/**
 * AlphaGateOrApp — after authentication, checks alpha_blocked from the
 * ensure-team call. Blocked users see AlphaGatePage and cannot reach any
 * app route. teamCheckComplete is true before this renders (AuthContextBridge
 * holds children until the call completes).
 */
function AlphaGateOrApp(): React.JSX.Element {
  const { alphaBlocked } = useAuth()
  if (alphaBlocked) {
    return <AlphaGatePage />
  }
  return <AppRoutes />
}

export default function App(): React.JSX.Element {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        <Route
          path="/*"
          element={
            <VanityOrgGate>
              <AuthGuard>
                <AlphaGateOrApp />
              </AuthGuard>
            </VanityOrgGate>
          }
        />
      </Routes>
      <Toaster />
    </BrowserRouter>
  )
}
